package com.evoapps.evoshift.domain.market.payroll

import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.RatePeriod
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable

/**
 * Per-person facts the payroll of a market needs, kept with the pay rates in the private
 * `users/{uid}/sensitive/{workplaceId}` document. Booleans rather than a birth date, so no more
 * personal data is stored than the calculation needs. Fields a market does not use are ignored.
 */
@Serializable
data class PayrollProfile(
    /** Under 26: income tax relief for young people (PL: "ulga dla młodych", PIT-0). */
    val under26: Boolean = false,
    /** Pupil or student under 26 (PL: civil-law contracts carry no social or health contributions). */
    val student: Boolean = false,
    /** Asked this payer to apply the monthly tax-free reduction (PL: PIT-2). */
    val taxReliefRequested: Boolean = true,
    /** Lives in another town than the workplace (PL: higher fixed costs of earning income). */
    val commutes: Boolean = false,
    /** Joined the voluntary sickness insurance (PL: civil-law contracts). */
    val voluntarySickness: Boolean = false,
    /** Already insured elsewhere at least at the minimum wage (PL: civil-law social contributions not due). */
    val insuredElsewhereAtMinimumWage: Boolean = false,
    /**
     * SH-57: turned 50 before this calendar year (PL: the employer pays 14 days of illness a year
     * instead of 33, Kodeks pracy art. 92 § 1).
     */
    val over50: Boolean = false,
    /** SH-57: at least 10 years of work, school included (PL: 26 days of annual leave instead of 20). */
    val longService: Boolean = false,
    /** SH-57: saves in PPK (pracownicze plany kapitałowe); false after opting out or when not enrolled. */
    val ppk: Boolean = false,
    /** SH-57: asked for the reduced PPK rate (0.5%) for a low income (at most 1.2 × the minimum wage). */
    val ppkReducedRate: Boolean = false,
    /**
     * SH-149: the last day ("YYYY-MM-DD", inclusive) [under26] applies, when known (the 26th
     * birthday); after it the relief is off without anyone changing the profile. Null: no known end.
     */
    val under26Until: String? = null,
    /** SH-149: the last day ("YYYY-MM-DD", inclusive) of the [student] status (end of studies); null: no known end. */
    val studentUntil: String? = null
) {
    /**
     * SH-149: these facts as they are on [date] ("YYYY-MM-DD"): a status past its known end is off.
     * A student status also ends with the under-26 one (it only counts until 26).
     */
    fun on(date: String): PayrollProfile {
        val day = date.take(10)
        fun valid(until: String?) = until.isNullOrBlank() || day <= until
        val young = under26 && valid(under26Until)
        val studying = student && valid(studentUntil) && valid(under26Until)
        return if (young == under26 && studying == student) this else copy(under26 = young, student = studying)
    }

    /** SH-149: each fact and whether it applies (the known ends left out). */
    val facts: Map<PayrollFact, Boolean>
        get() = mapOf(
            PayrollFact.UNDER_26 to under26,
            PayrollFact.STUDENT to student,
            PayrollFact.TAX_RELIEF to taxReliefRequested,
            PayrollFact.COMMUTES to commutes,
            PayrollFact.VOLUNTARY_SICKNESS to voluntarySickness,
            PayrollFact.INSURED_ELSEWHERE to insuredElsewhereAtMinimumWage,
            PayrollFact.OVER_50 to over50,
            PayrollFact.LONG_SERVICE to longService,
            PayrollFact.PPK to ppk,
            PayrollFact.PPK_REDUCED to ppkReducedRate
        )

    /** SH-149: the same facts, whatever their known ends ([under26Until], [studentUntil]). */
    fun sameFactsAs(other: PayrollProfile): Boolean = facts == other.facts

    /** SH-149: the known end ("YYYY-MM-DD") of [fact], if it has one. */
    fun knownEnd(fact: PayrollFact): String? = when (fact) {
        PayrollFact.UNDER_26 -> under26Until?.takeIf { under26 && it.isNotBlank() }
        PayrollFact.STUDENT -> studentUntil?.takeIf { student && it.isNotBlank() }
        else -> null
    }
}

/** SH-149: one of the facts of a [PayrollProfile], for listing what changed. */
enum class PayrollFact {
    UNDER_26, STUDENT, TAX_RELIEF, COMMUTES, VOLUNTARY_SICKNESS, INSURED_ELSEWHERE, OVER_50, LONG_SERVICE, PPK, PPK_REDUCED
}

/**
 * SH-149: payroll facts that apply from [effectiveFrom] ("YYYY-MM-DD"; "" = from the start) until
 * the next period starts.
 */
@Serializable
data class ProfilePeriod(val effectiveFrom: String, val profile: PayrollProfile)

/**
 * SH-149: the facts in force on [date] ("YYYY-MM-DD") among these periods, with their known ends
 * applied; before the first known start, the oldest; without periods, [fallback].
 */
fun List<ProfilePeriod>.profileOn(date: String, fallback: PayrollProfile = PayrollProfile()): PayrollProfile {
    val day = date.take(10)
    val sorted = sortedBy { it.effectiveFrom }
    val profile = sorted.lastOrNull { it.effectiveFrom <= day }?.profile ?: sorted.firstOrNull()?.profile ?: fallback
    return profile.on(day)
}

/** SH-149: the part of a month's pay ([weight], any unit) earned under one set of payroll facts. */
data class ProfileShare(val profile: PayrollProfile, val weight: Double)

/** Employer-level payroll settings of a workplace. */
@Serializable
data class WorkplacePayrollSettings(
    /** Accident insurance rate assigned to the employer (PL: "stopa procentowa składki wypadkowej"). */
    val accidentInsuranceRate: Double = DEFAULT_ACCIDENT_RATE,
    /** The workplace's night period ("HH:mm"); PL law: 8 hours between 21:00 and 7:00. */
    val nightStart: String = "22:00",
    val nightEnd: String = "06:00",
    /**
     * The owner is not paid for the shifts they work (owner's draw, not a wage): their shifts get
     * no cost snapshot and stay out of labour cost, payroll and their own earnings.
     */
    val ownerShiftsUnpaid: Boolean = false,
    /**
     * SH-147: the people confirmed as working here without pay (0 zł), e.g. a partner in the
     * business: like an unpaid owner, their shifts get no cost snapshot and stay out of labour
     * cost and payroll, and they never count as "no pay set". The owner needs no entry (their
     * missing pay never warns); [ownerShiftsUnpaid] keeps them out even with pay terms.
     */
    val unpaidUserIds: List<String> = emptyList(),
    /**
     * SH-232: when each person works here without pay (settings.unpaidPeriods, by user id), as the
     * app read it ([com.evoapps.evoshift.domain.budget.UnpaidWork.reconcile]: older app versions'
     * changes to [unpaidUserIds] taken in). Someone on [unpaidUserIds] without periods is unpaid
     * from the start. Written only through WorkplaceRepository.updateUnpaidWork, never by a
     * settings save (an operational manager's would have to leave it as it is).
     */
    val unpaidPeriods: Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>> = emptyMap(),
    /**
     * SH-57: the working-time settlement period in months (PL: okres rozliczeniowy, Kodeks pracy
     * art. 129 § 1, at most 4 months in the basic system), aligned to January: 3 = quarters.
     */
    val settlementPeriodMonths: Int = DEFAULT_SETTLEMENT_PERIOD_MONTHS,
    /**
     * SH-57: overtime (and Sunday or holiday work over the norm) is given back as time off
     * instead of paying the supplements (PL: czas wolny w zamian, Kodeks pracy art. 151²).
     */
    val overtimeTimeOffInLieu: Boolean = false,
    /**
     * SH-193: what an unassigned shift (demand nobody fills yet) is estimated to cost per hour in
     * the planned labour cost. Finances' choice: firestore.rules let only owners and financial
     * managers change settings.payroll.
     */
    val openShiftCost: OpenShiftCostSetting = OpenShiftCostSetting()
) {
    /** The months (1–12) of the settlement period [month] is in, aligned to January. */
    fun settlementMonths(month: Int): IntRange {
        val length = settlementPeriodMonths.coerceIn(1, 4)
        val first = (month - 1) / length * length + 1
        return first..minOf(first + length - 1, 12)
    }

    companion object {
        const val DEFAULT_ACCIDENT_RATE = 0.0167
        const val DEFAULT_SETTLEMENT_PERIOD_MONTHS = 1
        /** The settlement periods offered in the workplace settings. */
        val SETTLEMENT_PERIOD_CHOICES = listOf(1, 2, 3, 4)
    }
}

/** SH-193: how an unassigned shift's cost per hour is chosen. */
@Serializable
enum class OpenShiftCostMode {
    /**
     * Last month's employer cost per hour worked here; without a last month, this month's assigned
     * shifts' average (or the team's pay terms); then the [OpenShiftCostSetting.perHour] set; else none.
     */
    AUTOMATIC,
    /** Always [OpenShiftCostSetting.perHour]. */
    FIXED;

    companion object {
        fun fromId(id: String?): OpenShiftCostMode = entries.firstOrNull { it.name == id } ?: AUTOMATIC
    }
}

/** SH-193: the cost per hour unassigned shifts are estimated at in the plan. */
@Serializable
data class OpenShiftCostSetting(
    val mode: OpenShiftCostMode = OpenShiftCostMode.AUTOMATIC,
    /** Employer cost per hour in the workplace's currency: the fixed rate, or the automatic one's last fallback. */
    val perHour: Double? = null
) {
    /** [perHour] when it is a usable rate. */
    val validPerHour: Double? get() = perHour?.takeIf { isValidPerHour(it) }

    companion object {
        /** The largest rate the settings accept (and firestore.rules). */
        const val MAX_PER_HOUR = 100_000.0

        fun isValidPerHour(value: Double): Boolean = !value.isNaN() && value > 0 && value <= MAX_PER_HOUR
    }
}

/** SH-57: what kind of paid absence a day is. */
enum class LeaveKind {
    /** Sick leave paid at the usual rate (PL: 80%). */
    SICK,
    /** Sick leave paid in full (PL: 100%, pregnancy or an accident on the way to or from work). */
    SICK_FULL_PAY,
    /**
     * Annual leave (PL: urlop wypoczynkowy), including leave on demand (urlop na żądanie, art. 167(2)
     * of the Labour Code: up to 4 days of the annual leave taken at short notice, paid the same way).
     */
    VACATION,
    /**
     * SH-223: time off the employer pays in full without taking it from the annual leave: blood
     * donation, childcare (art. 188), urlop okolicznościowy, time off in lieu of overtime.
     */
    PAID_LEAVE,
    /** SH-223: time off the employer pays at half (PL: force majeure, art. 148¹ KP). */
    HALF_PAID_LEAVE,
    /** SH-223: time off nobody pays (unpaid leave, carer's leave, urlop wychowawczy). */
    UNPAID_LEAVE,
    /**
     * SH-223: time off ZUS pays a benefit for, the employer nothing (maternity, paternity and
     * parental leave, the care allowance). The benefit itself is not estimated.
     */
    BENEFIT_LEAVE;

    val isSick: Boolean get() = this == SICK || this == SICK_FULL_PAY
}

/**
 * SH-57: one calendar day of approved leave. SH-223: [hours] only part of the day (childcare or
 * force majeure in hours, time off in lieu); null the whole day.
 */
data class LeaveDay(val date: LocalDate, val kind: LeaveKind, val hours: Double? = null)

/** One stretch of paid work: a shift's planned times, or its clocked times once clocked. */
data class WorkedSpan(val start: LocalDateTime, val end: LocalDateTime)

/**
 * SH-57: one person's month at one workplace, with what the month's pay depends on beyond the
 * shifts themselves: overtime against the settlement period's norms, Sunday and holiday work,
 * and the minimum wage.
 */
data class MonthWorkInput(
    val year: Int,
    val month: Int,
    val contract: SystemContractType,
    val compensation: CompensationModel,
    /** Hourly pay: the monthly hours the person is employed for, if stated (PL: declared hours). */
    val declaredHours: Int? = null,
    /**
     * The person's paid work at this workplace from the start of the settlement period to the end
     * of [month]; work outside that range is ignored.
     */
    val shifts: List<WorkedSpan>,
    val settings: WorkplacePayrollSettings,
    /** Pay of the month beyond the shifts that counts towards the minimum wage (bonuses). */
    val otherCountedPay: Double = 0.0,
    /** Without [shifts]: the month's hours to assume, all within the norms (the pay form's estimate). */
    val assumedHours: Double? = null,
    /** SH-57: the person's facts (age over 50, years of work, PPK) for leave pay. */
    val profile: PayrollProfile = PayrollProfile(),
    /**
     * SH-57: the pay terms with the dates they apply from (FinancialEngagement.ratePeriods);
     * empty: [compensation] applies all month. A change inside the month is paid pro rata.
     */
    val ratePeriods: List<RatePeriod> = emptyList(),
    /**
     * SH-57: approved sick leave and annual leave of the calendar year up to the end of [month]
     * (earlier days count towards the employer's sick days and the leave used).
     */
    val leave: List<LeaveDay> = emptyList(),
    /** SH-57: the person's paid work here in the 12 months before [month] (sick-pay base, holiday pay averages). */
    val earlierWork: List<WorkedSpan> = emptyList(),
    /**
     * SH-149: the facts with the days they apply from (FinancialEngagement.profilePeriods); empty:
     * [profile] applies all month (its known ends still count).
     */
    val profilePeriods: List<ProfilePeriod> = emptyList(),
    /**
     * SH-223: approved work outside the shifts this month (overtime and work-event requests): in
     * the working-time maths like the shifts, and paid at the hourly rate (a salary covers it).
     */
    val extraWork: List<WorkedSpan> = emptyList()
) {
    /** SH-149: the facts in force on [date]. */
    fun profileOn(date: LocalDate): PayrollProfile =
        if (profilePeriods.isEmpty()) profile.on(date.toString()) else profilePeriods.profileOn(date.toString(), profile)
}

/**
 * What the month adds to the shifts' pay (SH-57). Hours are this month's: daily overtime where it
 * happens, the settlement period's excess in its last month. [extraGross] goes on top of the gross.
 */
data class MonthPayFactors(
    /** First and last month of the settlement period [MonthWorkInput.month] is in, as year * 12 + month - 1. */
    val periodStart: Int,
    val periodEnd: Int,
    /** The person's working-time norm for the whole period (FTE share), and the hours worked in it so far. */
    val periodNormHours: Double,
    val periodWorkedHours: Double,
    /** Whether the period ends with this month (its excess over the norm is settled now). */
    val settled: Boolean = true,
    /** Overtime hours with the 50% supplement (over the daily norm, by day, not at night nor on a Sunday or holiday). */
    val overtime50Hours: Double = 0.0,
    /** Overtime hours with the 100% supplement: at night, on Sundays and holidays, over the average weekly norm. */
    val overtime100Hours: Double = 0.0,
    /** Sunday and holiday hours over the norm, for which no other day off was left: the 100% supplement. */
    val sundayHolidayHours: Double = 0.0,
    /** The hourly base of the supplements. */
    val supplementBase: Double = 0.0,
    /** A monthly salary: the normal pay for the hours over the period's norm (hourly pay already has it). */
    val overtimePay: Double = 0.0,
    val overtime50Supplement: Double = 0.0,
    val overtime100Supplement: Double = 0.0,
    val sundayHolidaySupplement: Double = 0.0,
    /** Overtime given back as time off instead of the supplements (WorkplacePayrollSettings.overtimeTimeOffInLieu). */
    val timeOffInLieuHours: Double = 0.0,
    /** The minimum pay for the month (for the hours the person is employed for); 0 where it does not apply. */
    val minimumPay: Double = 0.0,
    /** Paid on top so that the month reaches [minimumPay]. */
    val minimumWageTopUp: Double = 0.0,
    /** A civil-law contract's hourly rate under the year's minimum hourly rate: a warning. */
    val minimumHourlyRate: Double? = null,

    // SH-57: sick leave (all estimates; ZUS and the accountant decide).
    /** Calendar days of sick leave this month. */
    val sickDays: Int = 0,
    /** Of those, the days the employer pays for (PL: wynagrodzenie chorobowe) and its amount. */
    val sickPayDays: Int = 0,
    val sickPay: Double = 0.0,
    /** Days past the employer's yearly limit, or on a civil-law contract: the ZUS sickness benefit. */
    val sickBenefitDays: Int = 0,
    val sickBenefit: Double = 0.0,
    /** The monthly base both are worked out from (the 12-month average less 13.71%). */
    val sickPayBase: Double = 0.0,
    /** A salary is not paid for the days of illness (PL: 1/30 a day). */
    val salaryReduction: Double = 0.0,

    // SH-57: annual leave.
    /** Working days and hours of annual leave this month. */
    val vacationDays: Int = 0,
    val vacationHours: Double = 0.0,
    /** Hourly pay for the leave hours (a salary already pays them: 0). */
    val holidayPay: Double = 0.0,
    /** The average variable pay (night, overtime) of the 3 months before, for the leave hours. */
    val holidayVariablePay: Double = 0.0,
    /** The year's leave entitlement (pro rata FTE) and the days taken so far this year; 0 without a contract with leave. */
    val leaveEntitlementDays: Int = 0,
    val leaveUsedDays: Int = 0,

    // SH-223: other time off (blood donation, childcare, force majeure, unpaid and ZUS-paid leave).
    /** Working days (whole or in part) and hours of time off the employer pays in full this month. */
    val paidLeaveDays: Int = 0,
    val paidLeaveHours: Double = 0.0,
    /** Working days and hours of time off paid at half (force majeure). */
    val halfPaidLeaveDays: Int = 0,
    val halfPaidLeaveHours: Double = 0.0,
    /** Hourly pay for that time off (a salary already pays it: 0). */
    val paidLeavePay: Double = 0.0,
    /** Working days of unpaid leave, and of leave ZUS pays a benefit for (not estimated). */
    val unpaidLeaveDays: Int = 0,
    val benefitLeaveDays: Int = 0,
    /** A salary is not paid for unpaid hours (salary / the month's norm × hours), nor half of the half-paid ones. */
    val unpaidLeaveReduction: Double = 0.0,
    /** SH-223: hourly pay for approved work outside the shifts (overtime and work events). */
    val extraWorkHours: Double = 0.0,
    val extraWorkPay: Double = 0.0,

    // SH-57: pay terms that changed during the month.
    /** The terms that applied this month, from the day each started; one entry: no change. */
    val rateChanges: List<RatePeriod> = emptyList(),
    /** What the pro-rata salary differs from the month-end salary by (hourly shifts are priced by their date). */
    val rateChangeAdjustment: Double = 0.0
) {
    val belowMinimumHourlyRate: Boolean get() = minimumHourlyRate != null
    val overtimeHours: Double get() = overtime50Hours + overtime100Hours
    val supplements: Double get() = overtime50Supplement + overtime100Supplement + sundayHolidaySupplement
    /** SH-57: pay for time not worked: sick pay, the ZUS benefit, holiday pay. */
    val leavePay: Double get() = sickPay + sickBenefit + holidayPay + holidayVariablePay + paidLeavePay
    val extraGross: Double get() = overtimePay + supplements + minimumWageTopUp + leavePay - salaryReduction - unpaidLeaveReduction +
        rateChangeAdjustment + extraWorkPay
    /** SH-57: the part of [extraGross] that carries no social contributions (sick pay and the benefit). */
    val contributionFreeGross: Double get() = sickPay + sickBenefit
    /** SH-57: the part of [extraGross] ZUS pays, not the employer. */
    val paidByZus: Double get() = sickBenefit
    /** Whether the pay terms changed during the month. */
    val rateChanged: Boolean get() = rateChanges.size > 1
    /** Whether there is anything to show. */
    val isEmpty: Boolean get() = extraGross == 0.0 && timeOffInLieuHours == 0.0 && overtimeHours == 0.0 && sundayHolidayHours == 0.0 &&
        !belowMinimumHourlyRate && sickDays == 0 && vacationDays == 0 && !rateChanged &&
        paidLeaveDays == 0 && halfPaidLeaveDays == 0 && unpaidLeaveDays == 0 && benefitLeaveDays == 0 && extraWorkHours == 0.0

    companion object {
        fun none(year: Int, month: Int): MonthPayFactors {
            val index = year * 12 + month - 1
            return MonthPayFactors(index, index, 0.0, 0.0)
        }
    }
}

/** One shift to be priced. */
data class ShiftPayInput(
    val contract: SystemContractType,
    val compensation: CompensationModel,
    val profile: PayrollProfile,
    val start: LocalDateTime,
    val end: LocalDateTime,
    val settings: WorkplacePayrollSettings,
    /** Expected monthly gross from this payer, used for thresholds that depend on the month (e.g. PL Labour Fund). */
    val expectedMonthlyGross: Double? = null,
    /** SH-116: unpaid break stretches within [start, end): not worked, not paid (BreakRules). */
    val unpaidBreaks: List<WorkedSpan> = emptyList()
)

/** What a shift costs. [employerCost] = [gross] + [employerContributions]. */
data class ShiftPay(
    val hours: Double,
    val hourlyRate: Double,
    val basePay: Double,
    val nightHours: Double,
    val nightAllowance: Double,
    val gross: Double,
    val employerContributions: Double,
    val employerCost: Double
)

enum class PayLineKind {
    PENSION, DISABILITY, SICKNESS, ACCIDENT, HEALTH, INCOME_TAX, LABOUR_FUND, GUARANTEE_FUND,
    /** SH-57: PPK, the employee's (deducted) and the employer's (on top, taxed as the employee's income). */
    PPK
}

data class PayLine(val kind: PayLineKind, val amount: Double)

/** Facts behind a payslip that the UI explains. */
enum class PayslipNote {
    /** Under-26 relief: no income tax on (part of) this month. */
    YOUTH_RELIEF,
    /** Student under 26 on a civil-law contract: no social or health contributions. */
    STUDENT_EXEMPT,
    /** Insured elsewhere: only health insurance due on this contract. */
    INSURED_ELSEWHERE,
    /** Pension and disability contributions stopped at the annual cap. */
    CONTRIBUTION_CAP_REACHED,
    /** A contractor invoices; taxes and contributions are theirs. */
    CONTRACTOR,
    /**
     * SH-133: the owner or a partner working without a contract: no contributions, and labour
     * cost only as the notional hourly amount set for them (0 by default).
     */
    OWNER_PARTNER,
    /** Rates for this year are not known yet; the latest known year was used. */
    RATES_ESTIMATED,
    /** SH-57: PPK at the reduced employee rate (0.5%). */
    PPK_REDUCED
}

/** One person's month at one payer. */
data class PayslipInput(
    val year: Int,
    val month: Int,
    val contract: SystemContractType,
    val profile: PayrollProfile,
    val gross: Double,
    val settings: WorkplacePayrollSettings,
    /** Gross already paid this year by this payer (annual caps and limits). */
    val grossSoFarThisYear: Double = 0.0,
    /**
     * SH-57: the part of [gross] without social contributions (PL: sick pay and the ZUS sickness
     * benefit carry health insurance and income tax only).
     */
    val contributionFreeGross: Double = 0.0,
    /** SH-57: the part of [gross] that someone else pays (PL: the ZUS sickness benefit): not the employer's cost. */
    val paidByOthers: Double = 0.0
)

data class Payslip(
    val gross: Double,
    /** Deducted from the gross: the person's contributions and income tax. */
    val employeeLines: List<PayLine>,
    /** Paid by the employer on top of the gross. */
    val employerLines: List<PayLine>,
    val net: Double,
    val employerCost: Double,
    val notes: Set<PayslipNote> = emptySet()
) {
    fun employee(kind: PayLineKind) = employeeLines.filter { it.kind == kind }.sumOf { it.amount }
    fun employer(kind: PayLineKind) = employerLines.filter { it.kind == kind }.sumOf { it.amount }
    val employerContributions: Double get() = employerLines.sumOf { it.amount }
}
